import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin
);

function CustomLineChart({ legends = [], chart_title = "", data = [], labels = [] }) {
    const [startIndex, setStartIndex] = useState(0);
    const maxPoints = 50;

    const handleSliderChange = (event) => {
        setStartIndex(parseInt(event.target.value, 10));
    };

    const filteredLabels = labels.slice(startIndex, startIndex + maxPoints);
    const filteredData = data.map(dataset => dataset.slice(startIndex, startIndex + maxPoints));

    const colors = [
        'rgba(79, 119, 170, 1)',
        'rgba(255, 165, 0,1)',
        'rgba(255, 99, 132,1)',
        'rgba(255, 205, 86,1)',
        'rgba(75, 192, 192,1)',
        'rgba(54, 162, 235,1)',
        'rgba(153, 102, 255,1)',
        'rgba(231,233,237,1)'
    ];

    const final_data = filteredData.map((dataset, index) => {
        const isBattery = legends[index].toLowerCase().includes("battery");
        const baseColor = colors[index % colors.length];
        const thresholdMinutes = isBattery ? 5 : 60;

        // Wrap data as { x, y } to use with segment logic
        const transformedData = dataset.map((y, i) => ({
            x: filteredLabels[i],
            y
        }));

        return {
            label: legends[index],
            data: transformedData,
            fill: false,
            borderColor: baseColor,
            backgroundColor: baseColor,
            pointRadius: 1,
            segment: {
                borderColor: ctx => {
                    const { p0, p1 } = ctx;
                    const x0 = p0.raw.x;
                    const x1 = p1.raw.x;

                    try {
                        const t0 = new Date(x0).getTime();
                        const t1 = new Date(x1).getTime();
                        const diffMinutes = (t1 - t0) / (1000 * 60);
                        return diffMinutes > thresholdMinutes ? "red" : baseColor;
                    } catch {
                        return baseColor;
                    }
                },
                borderWidth: 2
            }
        };
    });

    const chart_data = {
        labels: filteredLabels,
        datasets: final_data
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                text: chart_title,
                font: {
                    size: 20,
                    weight: "bold"
                },
                color: "black",
                padding: 0
            },
            legend: {
                labels: {
                    color: "black",
                    padding: 10,
                    generateLabels: (chart) => {
                        const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
                        const warningLegend = {
                            text: '⚠ No information received in the interval',
                            fillStyle: 'red',
                            strokeStyle: 'red',
                            lineWidth: 2,
                            hidden: false,
                            index: defaultLabels.length,
                            datasetIndex: null
                        };
                        return [...defaultLabels, warningLegend];
                    }
                }
            },
            zoom: {
                zoom: {
                    enabled: true,
                    mode: 'x',
                    speed: 0.1,
                    drag: true
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                    speed: 20,
                    threshold: 10
                }
            },
            elements: {
                point: {
                    radius: 0
                }
            }
        },
        scales: {
            x: {
                type: 'category',
                grid: {
                    color: "rgba(0,0,0,0.2)"
                },
                ticks: {
                    color: "black",
                    weight: "bold"
                }
            },
            y: {
                grid: {
                    color: "rgba(0,0,0,0.2)"
                },
                ticks: {
                    color: "black",
                    weight: "bold"
                }
            }
        }
    };

    useEffect(() => {
        setStartIndex(0);
    }, [data, labels]);

    return (
        <div style={{ overflowY: "hidden", height: "100%", width: "100%", background: "transparent", padding: "24px", boxSizing: 'border-box' }}>
            <Line data={chart_data} options={options} />
            <input
                type="range"
                min="0"
                max={Math.max(0, labels.length - maxPoints)}
                value={startIndex}
                onChange={handleSliderChange}
                step="1"
                style={{ width: "100%" }}
            />
        </div>
    );
}

export default CustomLineChart;
